import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Check,
  X,
  Coins
} from 'lucide-react';
import { formatCurrency, parseAmount } from '../../../utils/currency';

interface DiscrepancyItem {
  id: string;
  label: string;
  diff: number;
}

interface ClosureConfirmModalProps {
  isOpen: boolean;
  hasDiscrepancy: boolean;
  isArcheoBalanced: boolean;
  discrepanciesList: DiscrepancyItem[];
  assumeDiscrepancyChecked: boolean;
  userCashEntered: number;
  nextDayCash: string;
  cashWithdrawal: string;
  onAssumeChange: (checked: boolean) => void;
  onClose: () => void;
  onConfirm: (assumed: boolean) => void;
}

export function ClosureConfirmModal({
  isOpen,
  hasDiscrepancy,
  isArcheoBalanced,
  discrepanciesList,
  assumeDiscrepancyChecked,
  userCashEntered,
  nextDayCash,
  cashWithdrawal,
  onAssumeChange,
  onClose,
  onConfirm
}: ClosureConfirmModalProps) {
  const [isRendered, setIsRendered] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      setIsClosing(false);
    } else if (isRendered) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsRendered(false);
        setIsClosing(false);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isRendered]);

  if (!isRendered) return null;

  return (
    <div className="rcv-modal-backdrop">
      <div className={`rcv-modal ${isClosing ? 'rcv-modal--closing' : ''}`}>
        {/* Header */}
        <div className="rcv-modal-header">
          <div className="rcv-modal-header-left">
            <div className={`rcv-modal-icon ${hasDiscrepancy ? 'rcv-modal-icon--danger' : 'rcv-modal-icon--success'}`}>
              {hasDiscrepancy ? <AlertTriangle className="w-6 h-6" /> : <Check className="w-6 h-6" />}
            </div>
            <div>
              <h3 className="rcv-modal-title">
                {hasDiscrepancy ? 'Confirmar Cierre con Descuadre' : 'Confirmar Cierre de Caja'}
              </h3>
              <p className="rcv-modal-subtitle">
                {hasDiscrepancy ? 'Se han detectado descuadres entre los saldos físicos y de sistema.' : 'Todas las cifras cuadran perfectamente con el sistema.'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rcv-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="rcv-modal-content">
          {hasDiscrepancy ? (
            <>
              <div className="rcv-discrepancy-box">
                <h4 className="rcv-discrepancy-heading">Desglose de Descuadres Activos</h4>

                {/* Payment channels discrepancies */}
                {discrepanciesList.length > 0 && (
                  <div>
                    <p className="rcv-discrepancy-section-label">Diferencias en medios de pago:</p>
                    <div className="rcv-discrepancy-grid">
                      {discrepanciesList.map((d) => (
                        <div key={d.id} className="rcv-discrepancy-item">
                          <span className="rcv-discrepancy-item-label">{d.label}</span>
                          <span className="rcv-discrepancy-item-value">
                            {d.diff > 0 ? '+' : ''} {formatCurrency(d.diff)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Formula imbalances */}
                {!isArcheoBalanced && (
                  <div className="rcv-imbalance-section">
                    <p className="rcv-discrepancy-section-label">Fórmula de Arqueo (Disponible vs Asignado):</p>
                    <div className="rcv-imbalance-item">
                      <span className="rcv-imbalance-label">Imbalance de Arqueo</span>
                      <span className="rcv-imbalance-value">
                        Diferencia: {formatCurrency(userCashEntered - ((parseAmount(nextDayCash)) + (parseAmount(cashWithdrawal))))}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Assume Discrepancy block inside modal */}
              <div className="rcv-assume-block">
                <p className="rcv-assume-text">
                  Para poder completar el cierre de la jornada con diferencias, el cajero responsable debe asumir voluntariamente la responsabilidad por los descuadres detectados en este arqueo.
                </p>

                <label className="rcv-assume-label">
                  <input
                    type="checkbox"
                    checked={assumeDiscrepancyChecked}
                    onChange={(e) => onAssumeChange(e.target.checked)}
                    className="rcv-assume-checkbox"
                  />
                  <span className="rcv-assume-checkbox-text">
                    Asumo voluntariamente las inconsistencias y descuadres detallados para poder dar continuidad al cierre definitivo de la caja.
                  </span>
                </label>
              </div>
            </>
          ) : (
            <div className="rcv-success-state">
              <div className="rcv-success-icon">
                <Check className="w-10 h-10" />
              </div>
              <div>
                <h4 className="rcv-success-title">Caja Cuadrada Perfectamente</h4>
                <p className="rcv-success-desc">
                  Los montos introducidos coinciden al 100% con los cálculos de ventas guardados en el sistema del terminal POS. Puedes proceder a realizar el cierre fiscal de caja definitivo de la jornada de hoy.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="rcv-modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="rcv-cancel-btn"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={hasDiscrepancy && !assumeDiscrepancyChecked}
            onClick={() => onConfirm(hasDiscrepancy)}
            className={`rcv-confirm-btn ${
              hasDiscrepancy && !assumeDiscrepancyChecked
                ? 'rcv-confirm-btn--disabled'
                : 'rcv-confirm-btn--active'
            }`}
          >
            <Coins className="w-4 h-4" />
            Cerrar Caja Definitivo
          </button>
        </div>
      </div>
    </div>
  );
}
